'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import Link from "next/link";
import TourCard, { TourProps } from "@/components/ui/TourCard";
import TourCardSkeleton from "@/components/ui/TourCardSkeleton";
import { Swiper, SwiperSlide } from 'swiper/react';
import type { PageUrlMap } from "@/types/blog";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import { 
    discountContainerVariants,
    discountBoxVariants,
    discountTimerVariants,
    discountSwiperVariants,
    discountHoverVariants,
    timerHoverVariants,
    microHoverVariants,
    gridItemVariants,
    headerVariants,
    slideFromRightVariants
} from '@/lib/animations';
import 'swiper/css';

interface CategoryData {
    id: number;
    name?: string;
    slug?: string;
    description?: string;
    cover_image?: string | null;
    type?: string | null;
}

interface LinkData {
    type?: string;
    value?: string;
    label?: string;
    url?: string;
    slug?: string;
    templateKey?: string;
}

interface DiscountedToursProps {
    subTitle?: string;
    title?: string;
    categoryIds?: Array<number | string>;
    countdownDeadline?: string | null;
    languageId?: number;
    language?: string;
    languageCode?: string;
    enableClientFetch?: boolean;
    ctaLink?: LinkData | string | null;
    pageUrls?: PageUrlMap;
    __pageContext?: {
        languageId?: number;
        language?: string;
        languageCode?: string;
        pageUrls?: PageUrlMap;
    };
}

type CountdownState = {
    days: string;
    hours: string;
    minutes: string;
    seconds: string;
};

const DEFAULT_COUNTDOWN_SEGMENTS = [
    { key: 'days', label: 'DAYS', fallback: '845' },
    { key: 'hours', label: 'HOURS', fallback: '213' },
    { key: 'minutes', label: 'MINUTES', fallback: '347' },
    { key: 'seconds', label: 'SECONDS', fallback: '59' },
] as const;

const FALLBACK_CATEGORY_IMAGES = [
    'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1920&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&h=1200&fit=crop',
    'https://images.unsplash.com/photo-1519817914152-22d216bb9170?w=1920&h=1200&fit=crop',
];

const DEFAULT_TOURS: TourProps[] = [
    {
        src: [
            'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'Plaza de España Historical Building',
            'Plaza de España Architecture'
        ],
        type: 'Group Tour',
        imageCount: 5,
        videoCount: 2,
        title: 'Three Temples Bangkok City Tour',
        transferType: 'Bus',
        start: 'New York',
        stops: 'Los Angeles, Chicago, Miami',
        end: 'Miami',
        tourDuration: '5 Days',
        tag: 'Bestseller',
        price: {
            discount_price: '$169.00',
            price: '$199.00',
        },
        reviews: 1
    },
    {
        src: [
            'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'Maiden Tower at Sunset',
            'Maiden Tower Architecture'
        ],
        type: 'Private Tour',
        imageCount: 3,
        videoCount: 1,
        title: 'Istanbul Maiden Tower Experience',
        transferType: 'Private Transfer',
        start: 'Istanbul',
        stops: 'Bosphorus, Golden Horn',
        end: 'Istanbul',
        tourDuration: '3 Days',
        tag: 'Trending',
        price: {
            discount_price: '$299.00',
            price: '$349.00',
        },
        reviews: 2
    },
    {
        src: [
            'https://images.unsplash.com/photo-1512100356356-de1b84283e18?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'White Elephant Statue',
            'Traditional Thai Architecture'
        ],
        type: 'Group Tour',
        imageCount: 4,
        videoCount: 2,
        title: 'Thai Cultural Heritage Tour',
        transferType: 'Bus',
        start: 'Bangkok',
        stops: 'Ayutthaya, Sukhothai',
        end: 'Bangkok',
        tourDuration: '4 Days',
        tag: 'Featured',
        price: {
            discount_price: '$189.00',
            price: '$229.00',
        },
        reviews: 3
    },
    {
        src: [
            'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'Plaza de España Historical Building',
            'Plaza de España Architecture'
        ],
        type: 'Group Tour',
        imageCount: 5,
        videoCount: 2,
        title: 'Three Temples Bangkok City Tour',
        transferType: 'Bus',
        start: 'New York',
        stops: 'Los Angeles, Chicago, Miami',
        end: 'Miami',
        tourDuration: '5 Days',
        tag: 'Bestseller',
        price: {
            discount_price: '$169.00',
            price: '$199.00',
        },
        reviews: 1
    },
    {
        src: [
            'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'Maiden Tower at Sunset',
            'Maiden Tower Architecture'
        ],
        type: 'Private Tour',
        imageCount: 3,
        videoCount: 1,
        title: 'Istanbul Maiden Tower Experience',
        transferType: 'Private Transfer',
        start: 'Istanbul',
        stops: 'Bosphorus, Golden Horn',
        end: 'Istanbul',
        tourDuration: '3 Days',
        tag: 'Trending',
        price: {
            discount_price: '$299.00',
            price: '$349.00',
        },
        reviews: 2
    },
    {
        src: [
            'https://images.unsplash.com/photo-1512100356356-de1b84283e18?w=1920&h=1200&fit=crop',
            'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1920&h=1200&fit=crop'
        ],
        alt: [
            'White Elephant Statue',
            'Traditional Thai Architecture'
        ],
        type: 'Group Tour',
        imageCount: 4,
        videoCount: 2,
        title: 'Thai Cultural Heritage Tour',
        transferType: 'Bus',
        start: 'Bangkok',
        stops: 'Ayutthaya, Sukhothai',
        end: 'Bangkok',
        tourDuration: '4 Days',
        tag: 'Featured',
        price: {
            discount_price: '$189.00',
            price: '$229.00',
        },
        reviews: 3
    }
];

const formatNumber = (value: number) => value.toString().padStart(2, '0');

const calculateTimeLeft = (deadline?: string | null): CountdownState | null => {
    if (!deadline) {
        return null;
    }
    const target = new Date(deadline);
    if (Number.isNaN(target.getTime())) {
        return null;
    }
    const total = target.getTime() - Date.now();
    if (total <= 0) {
        return {
            days: '00',
            hours: '00',
            minutes: '00',
            seconds: '00',
        };
    }
    const days = Math.floor(total / (1000 * 60 * 60 * 24));
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((total / (1000 * 60)) % 60);
    const seconds = Math.floor((total / 1000) % 60);
    return {
        days: formatNumber(days),
        hours: formatNumber(hours),
        minutes: formatNumber(minutes),
        seconds: formatNumber(seconds),
    };
};

const normalizeCategoryIds = (ids?: Array<number | string>): number[] => {
    if (!Array.isArray(ids)) {
        return [];
    }
    return ids
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
};

const resolveCategoryImage = (image?: string | null, index = 0): string => {
    if (image && image.startsWith('http')) {
        return image;
    }
    if (image && image.trim().length > 0) {
        const normalized = image.replace(/^\/?storage\//, '');
        return `/storage/${normalized}`;
    }
    return FALLBACK_CATEGORY_IMAGES[index % FALLBACK_CATEGORY_IMAGES.length];
};

const mapCategoryToTour = (category: CategoryData, index: number): TourProps => {
    const image = resolveCategoryImage(category.cover_image || undefined, index);
    const safeName = category.name || `Kategori #${category.id}`;
    const productType = category.type || 'tour';
    const categorySlug = category.slug;
    return {
        src: [image],
        alt: [safeName],
        type: productType,
        imageCount: 1,
        videoCount: 0,
        title: safeName,
        transferType: 'Flexible',
        start: safeName,
        stops: category.description || 'Discover unforgettable experiences with this category.',
        end: safeName,
        tourDuration: 'Flexible',
        tag: 'Category',
        price: {
            discount_price: 'Contact Us',
            price: '—',
        },
        reviews: 0,
        tourId: `category-${category.id}`,
        slug: categorySlug,
        url: categorySlug ? `/${productType}/${categorySlug}` : undefined,
    };
};

export default function DiscountedTours(props?: DiscountedToursProps) {
    const {
        subTitle = 'Explore the world',
        title = 'Amazing Featured Tour Package The World',
        categoryIds,
        countdownDeadline,
        languageId: propLanguageId,
        language: propLanguage,
        languageCode: propLanguageCode,
        enableClientFetch = false,
        ctaLink,
        pageUrls: propPageUrls,
        __pageContext
    } = props || {};
    const languageId = propLanguageId || __pageContext?.languageId;
    const resolvedLanguageCode = propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;
    const pageUrls = propPageUrls || __pageContext?.pageUrls;
    const defaultLanguage = 'en';
    const normalizedCategoryIds = useMemo(
        () => normalizeCategoryIds(categoryIds),
        [categoryIds]
    );
    const hasCategorySelection = normalizedCategoryIds.length > 0;

    const [categoryTours, setCategoryTours] = useState<TourProps[] | null>(null);
    const [categoryFetchState, setCategoryFetchState] = useState<{ loading: boolean; error: string | null }>({ loading: false, error: null });
    const [timeLeft, setTimeLeft] = useState<CountdownState | null>(() => calculateTimeLeft(countdownDeadline));

    useEffect(() => {
        if (!countdownDeadline) {
            setTimeLeft(null);
            return;
        }
        const updateCountdown = () => {
            setTimeLeft(calculateTimeLeft(countdownDeadline));
        };
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [countdownDeadline]);

    useEffect(() => {
        // TODO: Product kategorileri endpoint'leri hazır olduğunda bu kısmı aktif et
        // Şimdilik API çağrısını devre dışı bırakıyoruz
        if (!hasCategorySelection || !enableClientFetch) {
            setCategoryTours(null);
            setCategoryFetchState({ loading: false, error: null });
            return;
        }
        
        // API endpoint'leri hazır olana kadar kategori fetch'ini devre dışı bırak
        // Fallback olarak DEFAULT_TOURS kullanılacak
        setCategoryTours(null);
        setCategoryFetchState({ loading: false, error: null });
        
        /* 
        // Product endpoint'leri hazır olduğunda bu kodu aktif et:
        let cancelled = false;
        const controller = new AbortController();

        async function fetchCategories() {
            try {
                setCategoryFetchState({ loading: true, error: null });
                const params = new URLSearchParams();
                if (languageId) {
                    params.set('language_id', String(languageId));
                }
                normalizedCategoryIds.forEach((id) => params.append('category_ids[]', String(id)));
                const response = await fetch(`/api/product-categories?${params.toString()}`, {
                    signal: controller.signal,
                });
                if (!response.ok) {
                    const errorPayload = await response.json().catch(() => ({}));
                    const message = errorPayload?.message || errorPayload?.error || `API error: ${response.status}`;
                    throw new Error(message);
                }
                const payload = await response.json();
                if (cancelled) {
                    return;
                }
                const categories: CategoryData[] = Array.isArray(payload?.data) ? payload.data : [];
                setCategoryTours(categories.map((category, index) => mapCategoryToTour(category, index)));
                setCategoryFetchState({ loading: false, error: null });
            } catch (error) {
                if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) {
                    return;
                }
                console.error('DiscountedTours kategoriler yüklenirken hata:', error);
                setCategoryTours([]);
                setCategoryFetchState({
                    loading: false,
                    error: error instanceof Error ? error.message : 'Kategoriler yüklenemedi',
                });
            }
        }

        fetchCategories();
        return () => {
            cancelled = true;
            controller.abort();
        };
        */
    }, [hasCategorySelection, enableClientFetch, normalizedCategoryIds, languageId]);

    const shouldShowLoader = hasCategorySelection && categoryFetchState.loading;
    const shouldShowError = hasCategorySelection && !categoryFetchState.loading && !!categoryFetchState.error;
    const shouldShowEmpty = hasCategorySelection && !categoryFetchState.loading && !categoryFetchState.error && (categoryTours?.length === 0);

    const toursToRender = !hasCategorySelection ? DEFAULT_TOURS : (categoryTours || []);

    const timerSegments = DEFAULT_COUNTDOWN_SEGMENTS.map((segment) => ({
        label: segment.label,
        value: timeLeft ? timeLeft[segment.key as keyof CountdownState] : segment.fallback,
    }));

    const countdownDeadlineLabel = useMemo(() => {
        if (!countdownDeadline) {
            return null;
        }
        const date = new Date(countdownDeadline);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    }, [countdownDeadline]);

    const normalizedCtaLink = useMemo<LinkData | null>(() => {
        if (!ctaLink) {
            return null;
        }
        if (typeof ctaLink === 'string') {
            return { url: ctaLink, label: ctaLink };
        }
        return ctaLink;
    }, [ctaLink]);

    // CTA href'i oluştur (pageUrls kontrolü ile)
    const ctaHref = useMemo(() => {
        if (!normalizedCtaLink) {
            return '#';
        }
        
        // Backend'den gelen URL varsa kullan
        if (normalizedCtaLink.url) {
            return normalizedCtaLink.url;
        }
        
        // pageUrls kontrolü ile dinamik URL oluştur
        if (pageUrls && normalizedCtaLink.type && normalizedCtaLink.value && resolvedLanguageCode) {
            const linkType = normalizedCtaLink.type;
            const linkValue = normalizedCtaLink.value;
            
            // Custom link ise direkt value'yu kullan
            if (linkType === 'custom') {
                return linkValue;
            }
            
            // pageUrls'den base URL al
            const baseUrl = resolveUrlFromMap(
                pageUrls,
                linkType === 'page' ? linkValue : linkType,
                resolvedLanguageCode,
                defaultLanguage,
                linkType === 'page' ? `/${linkValue}` : `/${linkType}`
            );
            
            // buildLocalizedLink ile slug'ı birleştir
            return buildLocalizedLink(baseUrl, linkValue, defaultLanguage);
        }
        
        // Fallback: slug veya value varsa kullan
        if (normalizedCtaLink.slug) {
            return normalizedCtaLink.slug;
        }
        if (normalizedCtaLink.value) {
            const linkType = normalizedCtaLink.type || 'page';
            return linkType === 'custom' 
                ? normalizedCtaLink.value 
                : `/${linkType}/${normalizedCtaLink.value}`;
        }
        
        return '#';
    }, [normalizedCtaLink, pageUrls, resolvedLanguageCode, defaultLanguage]);
    
    const ctaLabel = normalizedCtaLink?.label || 'EXPLORE MORE';

    return (
        <section
            className="relative overflow-hidden min-h-[930px] bg-no-repeat bg-alsero-discount-hero flex items-center after:content-[''] after:absolute after:inset-x-0 after:-bottom-[30px] after:h-[56px] after:bg-top after:bg-repeat-x after:bg-[length:auto_56px] after-alsero-white-line-56"
        >
            <div className="w-full grid grid-cols-12 items-center min-h-[600px] relative">
                <div className="col-span-12 lg:col-span-6 xl:col-span-4 xl:col-start-2">
                    <motion.div 
                        variants={discountContainerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        className="xl:w-9/12 me-auto font-sans"
                    >
                        <motion.div className="w-full" variants={headerVariants}>
                            {subTitle && (
                            <motion.span 
                                className="text-alsero-first font-medium text-base sm:text-lg md:text-xl font-display"
                                data-prop-path="subTitle"
                                variants={microHoverVariants}
                                initial="rest"
                                whileHover="hover"
                            >
                                    {subTitle}
                            </motion.span>
                            )}
                            <motion.h2
                                className="font-bold text-alsero-text-dark text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight"
                                data-prop-path="title"
                            >
                                {title}
                            </motion.h2>
                        </motion.div>
                       <div className="w-10/12 lg:mt-[140px] flex flex-col gap-5">
                           <motion.div 
                               variants={discountBoxVariants}
                               className="flex items-center gap-6 mb-8"
                           >
                               <div className="flex flex-row justify-between items-center">
                                   <motion.div 
                                       className="p-7 bg-white rounded-lg flex flex-col gap-2 items-center justify-center shadow-sm"
                                       variants={discountHoverVariants}
                                       initial="rest"
                                       whileHover="hover"
                                   >
                                       <span className="text-3xl lg:text-5xl font-bold text-alsero-first">25%</span>
                                       <div className="text-lg font-medium text-alsero-first">Off</div>
                                   </motion.div>
                                   <motion.p 
                                       className="text-lg w-7/12 text-alsero-text-dark font-semibold"
                                       variants={slideFromRightVariants}
                                       initial="hidden"
                                       whileInView="visible"
                                       viewport={{ once: true }}
                                   >
                                       Discover Great <span className="text-alsero-first font-bold">Discount</span> Deals Around The World
                                   </motion.p>
                               </div>
                           </motion.div>
                           <motion.div 
                               variants={headerVariants}
                               className="flex flex-col gap-4"
                           >
                               <div className="flex flex-row items-center justify-between">
                                   <motion.div 
                                       className="flex flex-col gap-2"
                                       variants={slideFromRightVariants}
                                       initial="hidden"
                                       whileInView="visible"
                                       viewport={{ once: true }}
                                   >
                                       <h3 className="text-2xl font-semibold text-alsero-text-dark">Hurry Up!</h3>
                                       <p className="text-alsero-light-gray text-sm font-medium">Offer Ends In:</p>
                                   </motion.div>
                                   <div className="flex gap-4">
                                       {timerSegments.map((item, index) => (
                                           <motion.div
                                               key={item.label}
                                               custom={index}
                                               variants={discountTimerVariants(index)}
                                               initial="hidden"
                                               whileInView="visible"
                                               viewport={{ once: true }}
                                               className="flex flex-col gap-2 items-center justify-center"
                                           >
                                                <motion.div 
                                                    className="bg-white rounded-full flex flex-col gap-2 items-center justify-center h-[60px] w-[60px] shadow-sm"
                                                    variants={timerHoverVariants}
                                                    initial="rest"
                                                    whileHover="hover"
                                                >
                                                    <span className="text-lg font-bold text-alsero-first">{item.value}</span>
                                                </motion.div>
                                               <span className="text-sm text-alsero-text-dark font-semibold">{item.label}</span>
                                           </motion.div>
                                       ))}
                                   </div>
                               </div>
                           </motion.div>
                           {countdownDeadlineLabel && (
                               <span className="text-xs text-alsero-light-gray">
                                   Kampanya bitişi: {countdownDeadlineLabel}
                               </span>
                           )}
                           <motion.div 
                               variants={headerVariants}
                               className="mt-10"
                           >
                               <motion.div
                                   variants={microHoverVariants}
                                   initial="rest"
                                   whileHover="hover"
                                   whileTap="tap"
                               >
                                   <Link href={ctaHref} className="inline-block text-center bg-alsero-first hover:bg-alsero-text-dark transition-all ease-in-out duration-300 rounded-lg text-white uppercase tracking-[%5] self-start px-12 py-6 shadow-md hover:shadow-xl">
                                       {ctaLabel}
                                   </Link>
                               </motion.div>
                           </motion.div>
                       </div>
                    </motion.div>
                </div>
                <div className="col-span-12 lg:col-span-6 xl:col-span-6 xl:col-start-7 relative">
                    <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2">
                        <motion.div
                            variants={discountSwiperVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                            className="relative"
                        >
                            {shouldShowLoader && (
                                <Swiper
                                    spaceBetween={16}
                                    slidesPerView={1}
                                    breakpoints={{
                                        640: {
                                            slidesPerView: 1.5,
                                            spaceBetween: 16,
                                        },
                                        768: {
                                            slidesPerView: 2,
                                            spaceBetween: 20,
                                        },
                                        1024: {
                                            slidesPerView: 2.5,
                                            spaceBetween: 24,
                                        },
                                        1280: {
                                            slidesPerView: 2.5,
                                            spaceBetween: 24,
                                        }
                                    }}
                                    loop={false}
                                    className="w-full h-full py-4"
                                >
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <SwiperSlide key={index} className="h-full">
                                            <TourCardSkeleton index={index} viewType="box" />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            )}
                            {shouldShowError && (
                                <div className="flex items-center justify-center min-h-[420px] rounded-2xl bg-white/80 text-red-500 text-sm text-center px-6">
                                    {categoryFetchState.error}
                                </div>
                            )}
                            {shouldShowEmpty && (
                                <div className="flex items-center justify-center min-h-[420px] rounded-2xl bg-white/80 text-alsero-light-gray text-sm text-center px-6">
                                    Seçilen kategoriler için tur bulunamadı.
                                </div>
                            )}
                            {(!hasCategorySelection || (categoryTours !== null && categoryTours.length > 0)) && (
                                <Swiper
                                    spaceBetween={16}
                                    slidesPerView={1}
                                    breakpoints={{
                                        640: {
                                            slidesPerView: 1.5,
                                            spaceBetween: 16,
                                        },
                                        768: {
                                            slidesPerView: 2,
                                            spaceBetween: 20,
                                        },
                                        1024: {
                                            slidesPerView: 2.5,
                                            spaceBetween: 24,
                                        },
                                        1280: {
                                            slidesPerView: 2.5,
                                            spaceBetween: 24,
                                        }
                                    }}
                                    loop={toursToRender.length > 1}
                                    className="w-full h-full py-4"
                                >
                                    {toursToRender.map((tour, index) => (
                                        <SwiperSlide key={`${tour.title}-${index}`} className="h-full">
                                            <motion.div
                                                variants={gridItemVariants}
                                                initial="hidden"
                                                whileInView="visible"
                                                viewport={{ once: true }}
                                            >
                                                <TourCard 
                                                    tour={tour} 
                                                    viewType="box" 
                                                    index={index}
                                                    languageId={languageId}
                                                    language={resolvedLanguageCode}
                                                    languageCode={resolvedLanguageCode}
                                                    pageUrls={pageUrls}
                                                    defaultLanguage={defaultLanguage}
                                                />
                                            </motion.div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    )
}